import React from 'react'
import {
  useAuth,
  useLatestLotteryResult,
  useUserSubscription,
  useUserGeneratedNumbers,
  useUserWinningStats,
} from '@workspace/public-utils'
import { motion } from 'framer-motion'
import { Seo } from '../../components/Seo'

// LotteryBall component for displaying numbers with Korean lottery colors
const LotteryBall = ({ number, isBonus = false }: { number: number; isBonus?: boolean }) => {
  const getNumberStyle = (num: number) => {
    if (isBonus) return "bg-gradient-to-br from-purple-500 to-purple-700 text-white"
    if (num <= 5) return "text-white"
    if (num <= 10) return "text-white"
    if (num <= 15) return "text-white"
    if (num <= 20) return "text-white"
    if (num <= 25) return "text-white"
    if (num <= 30) return "text-white"
    if (num <= 35) return "text-white"
    if (num <= 40) return "text-white"
    return "text-white"
  }

  const getBackgroundColor = (num: number) => {
    if (isBonus) return { background: 'linear-gradient(135deg, #9333ea 0%, #6b21a8 100%)' }
    if (num <= 5) return { background: 'linear-gradient(135deg, #FB2C36 0%, #d91f28 100%)' }
    if (num <= 10) return { background: 'linear-gradient(135deg, #ff692A 0%, #e5521a 100%)' }
    if (num <= 15) return { background: 'linear-gradient(135deg, #FFFF00 0%, #e6e600 100%)' }
    if (num <= 20) return { background: 'linear-gradient(135deg, #33FF33 0%, #2de62d 100%)' }
    if (num <= 25) return { background: 'linear-gradient(135deg, #03A9F4 0%, #0288d1 100%)' }
    if (num <= 30) return { background: 'linear-gradient(135deg, #AB47BC 0%, #8e24aa 100%)' }
    if (num <= 35) return { background: 'linear-gradient(135deg, #00FFFF 0%, #00e6e6 100%)' }
    if (num <= 40) return { background: 'linear-gradient(135deg, #F6339A 0%, #d91f7f 100%)' }
    return { background: 'linear-gradient(135deg, #02F29E 0%, #01d98c 100%)' }
  }

  return (
    <motion.div
      className={`${getNumberStyle(number)} inline-flex items-center justify-center w-10 h-10 rounded-full text-sm font-bold shadow-lg`}
      style={getBackgroundColor(number)}
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: (number % 6) * 0.05 }}
      whileHover={{ scale: 1.1 }}
    >
      {number}
    </motion.div>
  )
}

const DashboardPage: React.FC = () => {
  const { user } = useAuth()
  const { data: latestResult, isLoading: lotteryLoading } = useLatestLotteryResult()
  const { data: subscription, isLoading: subscriptionLoading } = useUserSubscription()
  const { data: recentNumbers, isLoading: numbersLoading } = useUserGeneratedNumbers(0, 5)
  const { data: winningStats, isLoading: winningLoading } = useUserWinningStats()
  const seo = <Seo title="대시보드" canonicalPath="/dashboard" noindex />

  return (
    <div>
      {seo}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">대시보드</h1>
        <p className="text-gray-600 dark:text-gray-400">안녕하세요, {user?.name || '게스트'}님!</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">이번 주 생성된 번호</h3>
            <p className="text-3xl font-bold text-blue-600">
              {numbersLoading ? '...' : (recentNumbers?.generated_numbers?.length || 0)}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">세트</p>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">현재 구독</h3>
            <p className="text-3xl font-bold text-purple-600">
              {subscriptionLoading ? '...' : (subscription?.subscription?.tier_name || 'None')}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">플랜</p>
          </div>
          {/* Winning Statistics card placeholder - will fetch from backend when available */}
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">나의 당첨 통계</h3>
            {winningLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">불러오는 중...</p>
            ) : winningStats ? (
              <>
                <p className="text-3xl font-bold text-green-600">
                  {winningStats.total_wins?.toLocaleString('ko-KR') || 0}건
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  최고 등수 {winningStats.best_prize_name || (winningStats.best_prize_rank ? `${winningStats.best_prize_rank}등` : '-')}
                  {' · '}최근 {winningStats.last_win_round ? `${winningStats.last_win_round}회차` : '기록 없음'}
                </p>
                {winningStats.recent_wins && winningStats.recent_wins.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {winningStats.recent_wins.slice(0, 2).map((win, idx) => (
                      <div key={`${win.round_number}-${idx}`} className="flex items-center justify-between text-sm text-gray-700 dark:text-gray-300">
                        <span>{win.round_number}회차</span>
                        <span className="font-semibold text-green-600">{win.prize_name || `${win.prize_rank}등`}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">최근 당첨 기록이 없습니다.</p>
                )}
              </>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">당첨 통계를 불러올 수 없습니다.</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">최근 생성된 번호</h3>
            <div className="space-y-3">
              {numbersLoading ? (
                <div className="animate-pulse">
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <div className="flex space-x-2">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                      ))}
                    </div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
                  </div>
                </div>
              ) : recentNumbers?.generated_numbers && recentNumbers.generated_numbers.length > 0 ? (
                recentNumbers.generated_numbers.slice(0, 3).map((numberSet: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                    <div className="flex space-x-2">
                      {numberSet.numbers?.map((num: number, idx: number) => (
                        <LotteryBall key={idx} number={num} />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(numberSet.generated_at).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">아직 생성된 번호가 없습니다.</p>
              )}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow dark:shadow-none dark:border dark:border-gray-800 p-6" data-testid="lottery-result">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">최신 로또 결과</h3>
            {lotteryLoading ? (
              <div className="animate-pulse p-4 bg-yellow-50 dark:bg-gray-800 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
                </div>
                <div className="flex space-x-2 mb-2">
                  {[...Array(7)].map((_, i) => (
                    <div key={i} className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                  ))}
                </div>
              </div>
            ) : latestResult ? (
              <div className="p-4 bg-yellow-50 dark:bg-gray-800 rounded">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {latestResult.round_number}회 당첨번호
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(latestResult.draw_date).toLocaleDateString('ko-KR')}
                  </span>
                </div>
                <div className="flex space-x-2 mb-2" data-testid="lottery-numbers">
                  {latestResult.numbers?.map((num: number, index: number) => (
                    <LotteryBall key={index} number={num} />
                  ))}
                  {latestResult.bonus_number && (
                    <>
                      <span className="text-sm text-gray-500 dark:text-gray-400 mx-2 flex items-center">+</span>
                      <LotteryBall number={latestResult.bonus_number} isBonus={true} />
                    </>
                  )}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  1등: {latestResult.first_prize_winners}명 | 
                  상금: {latestResult.first_prize_amount ? `${Math.round(latestResult.first_prize_amount / 100000000)}억원` : '-'}
                </div>
              </div>
            ) : (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded text-center text-gray-500 dark:text-gray-400">
                로또 결과를 불러올 수 없습니다.
              </div>
            )}
          </div>
        </div>
    </div>
  )
}

export default DashboardPage